export const traceabilityEn = {
  title: 'Lot genealogy and traceability',
  subtitle:
    'Trace consumed and produced lots across all production levels and assess recall impacts.',
  help: { open: 'Open help for lot genealogy and recall simulations' },
  tabs: { trace: 'Trace lots', simulations: 'Recall simulations' },
  filters: {
    title: 'Trace filters',
    description:
      'Select at least one lot, product, partner, or production order. Filters are always tenant-scoped.',
    batch: 'Lot',
    batchNumber: 'Lot number contains',
    product: 'Product',
    supplier: 'Supplier',
    customer: 'Customer',
    productionOrder: 'Production order',
    from: 'Received from',
    to: 'Received until',
    direction: 'Direction',
    depth: 'Maximum levels',
    any: 'Any',
  },
  directions: {
    FORWARD: 'Forward to finished products',
    BACKWARD: 'Backward to raw materials',
    BOTH: 'Both directions',
  },
  relations: {
    ANCHOR: 'Starting lot',
    UPSTREAM: 'Upstream material',
    DOWNSTREAM: 'Downstream product',
    BOTH: 'Connected both ways',
  },
  batchStatuses: {
    FREE: 'Released',
    QUARANTINE: 'Quarantine',
    BLOCKED: 'Blocked',
    EXPIRED: 'Expired',
    DEPLETED: 'Depleted',
  },
  actions: {
    calculate: 'Run trace',
    reset: 'Reset filters',
    createSimulation: 'Create recall simulation',
    review: 'Review',
    cancel: 'Cancel',
    confirmBlocks: 'Block affected lots',
    export: 'Export CSV',
  },
  messages: {
    invalidFilter: 'The trace filters are incomplete.',
    simulationCreated: 'Recall simulation created from an immutable snapshot.',
    review: 'Recall simulation reviewed.',
    cancel: 'Recall simulation cancelled.',
    blocksConfirmed: 'Affected on-hand lots were blocked.',
  },
  warnings: {
    truncatedTitle: 'Result limit reached',
    truncated:
      'The graph was truncated at the configured depth or safety limit. Narrow the filters or increase the depth.',
  },
  stats: {
    lots: 'Affected lots',
    productionOrders: 'Production orders',
    deliveries: 'Deliveries',
    customers: 'Customers',
  },
  graph: {
    title: 'Multi-level material flow',
    description: 'Arrows follow material flow from consumed lots to produced lots.',
    ariaLabel: 'Graph of upstream and downstream lots',
    snapshotTitle: 'Frozen genealogy snapshot',
    snapshotDescription:
      'This graph is the exact evidence captured when the simulation was created.',
  },
  impact: {
    title: 'Affected stock, documents, and partners',
    description: 'The impact is derived from the complete traced lot set.',
    inventory: 'On-hand inventory',
    documents: 'Documents and deliveries',
    partners: 'Partners',
    productionOrders: 'Production orders',
    suppliers: 'Suppliers',
    customers: 'Customers',
    snapshotTitle: 'Frozen impact assessment',
  },
  columns: {
    article: 'Product',
    batch: 'Lot',
    relation: 'Relation',
    status: 'Status',
    warehouse: 'Warehouse / location',
    available: 'Available',
    document: 'Document',
    type: 'Type',
    customer: 'Customer',
    date: 'Date',
    batches: 'Lots',
    productionOrder: 'Production order',
    product: 'Product',
    completed: 'Completed',
  },
  empty: {
    inventory: 'No affected on-hand inventory.',
    documents: 'No affected documents.',
    partners: 'No affected partners.',
    orders: 'No affected production orders.',
    trace: 'Choose trace filters to display the lot genealogy.',
    simulations: 'No recall simulations found.',
    selectSimulation: 'Select a recall simulation.',
  },
  simulations: {
    search: 'Search number, title, or reason',
    allStatuses: 'All statuses',
    title: 'Saved simulations',
    description: 'Every entry retains its graph, impact, filters, and SHA-256 evidence hash.',
    noNotificationTitle: 'No external notification',
    noNotification:
      'This workflow does not notify customers, suppliers, or authorities. External communication requires a separate, explicit and authorized action.',
  },
  statuses: {
    DRAFT: 'Draft',
    REVIEWED: 'Reviewed',
    BLOCKS_CONFIRMED: 'Lots blocked',
    CANCELLED: 'Cancelled',
  },
  fields: {
    title: 'Title',
    reason: 'Reason',
    note: 'Decision note',
    createdAt: 'Created',
    direction: 'Trace direction',
    snapshotHash: 'Snapshot SHA-256',
    blockedBatches: 'Blocked lots',
  },
  create: {
    title: 'Create recall simulation',
    description:
      'The current graph and impact are frozen as an immutable snapshot. No lot is blocked yet.',
  },
  workflow: {
    review: {
      title: 'Review recall simulation',
      description: 'Confirm that the frozen impact has been checked against available evidence.',
    },
    cancel: {
      title: 'Cancel recall simulation',
      description: 'The snapshot remains available for audit; no affected lots will be changed.',
    },
  },
  block: {
    title: 'Explicitly block affected lots',
    description: '{{count}} affected on-hand lot(s) will be evaluated and blocked transactionally.',
    notificationTitle: 'External communication is excluded',
    notificationDescription:
      'Blocking stock does not send emails, messages, or regulatory notifications.',
    confirmLots: 'I explicitly confirm that all affected on-hand lots shall be blocked.',
    confirmNoNotification: 'I understand that no external notification is sent by this action.',
  },
} as const;
